export interface CardContent {
  title: string;
  intro?: string;         // Optional intro paragraph
  content: string | string[]; // Main content (paragraph or bullets)
  step: string;
  author?: string;
  role?: string;
}

export interface CaseStudy {
  id: string;
  title: string;
  subtitle: string;
  category: string;
  cards: CardContent[];
}

export const caseStudies: CaseStudy[] = [
  {
    id: "ux-design",
    title: "(Customer Discovery)",
    subtitle: "Making Sense of the Signals",
    category: "Customer Discovery",
    cards: [
      {
        title: "Challenge",
        content: "Everest Effect was an early-stage startup with an ambitious goal: to help public and private sector organizations deliver relief more effectively in times of crisis. But to unlock market traction and commercial viability, they first needed to deeply understand who their customers were - and what they really needed.",
        step: "01"
      },
      {
        title: "Approach",
        intro: "I led Everest Effect's customer discovery efforts to validate demand, clarify product-market fit, and accelerate commercialization.",
        content: [
          "Conducting interviews and qualitative research with key stakeholders",
          "Analyzing individual-level and geospatial data to surface unmet needs and high-impact use cases",
          "Synthesizing insights into actionable opportunity areas and product hypotheses"
        ],
        step: "02"
      },
      {
        title: "Results",
        content: "Customer discovery led to a sharper product vision, stronger stakeholder engagement, and a clear path to revenue. By identifying and prioritizing the most valuable customer segments and use cases, we enabled Everest Effect to move beyond theory and into real-world implementation - laying the foundation for growth in both the enterprise and public sector markets.",
        step: "03"
      },
      {
        title: "Testimonial",
        content: "Addy brought structure to ambiguity, surfacing real customer insights that shaped both our go-to-market and product direction. She quickly helped us validate use cases, prioritize high-impact segments, and launch multiple pilots. Addy has that rare ability to think at the systems level while executing with urgency. If you're navigating uncertainty or trying to find product-market fit, you want Addy on your side.",
        author: "Paul Gilson",
        role: "CEO of Everest Effect",
        step: "04"
      }
    ]
  },
  {
    id: "product-strategy",
    title: "Turning Vision Into Value",
    subtitle: "Merity – Professional Development Platform",
    category: "Product Strategy",
    cards: [
      {
        title: "Challenge",
        content: "Merity needed an experienced product partner to assess and enhance the user journeys across their core sites which were used by members, coaches, and company admins. The team wanted to ensure that each experience worked efficiently, aligned with user expectations, and supported the company's dual go-to-market strategy (B2C and B2B).",
        step: "01"
      },
      {
        title: "Approach",
        intro: "With a focus on aligning user experience to business goals, I got to work:",
        content: [
          "Mapping end-to-end workflows across user types and workflows",
          "Identifying critical pain points and disconnects between intent and execution",
          "Defining the ideal user journey and prioritize improvements",
          "Translating insights into actionable user stories"
        ],
        step: "02"
      },
      {
        title: "Results",
        content: "The work laid the foundation for Merity's successful launch across both consumer and enterprise markets - targeting a $100B+ serviceable addressable market in the U.S. By aligning platform experience with strategic goals, we enabled Merity to scale with confidence and clarity.",
        step: "03"
      },
      {
        title: "Testimonial",
        content: "Addy helped us go from a set of loosely connected features to a product that felt intentional and grounded in real user needs. Her ability to quickly diagnose problems, structure the work, and collaborate across teams was invaluable. I wish she had been part of our team from Day 1 - we would have saved a ton of money and time!",
        author: "Sandy Sabean",
        role: "CEO of Merity",
        step: "04"
      }
    ]
  },
  {
    id: "digital-transformation",
    title: "(MVP Product Build)",
    subtitle: "From Zero to Launch in 4 Months",
    category: "MVP Build Support",
    cards: [
      {
        title: "Challenge",
        content: "ORKA Finance was a seed-funded startup with a big mission: to help small businesses benefit from energy independence through solar. But to deliver on that promise, they needed to build a scalable, digital-first lending platform - fast.",
        step: "01"
      },
      {
        title: "Approach",
        intro: "I translated the company's business goals into a clear, actionable product roadmap and led a cross-functional team through an accelerated MVP build. This included:",
        content: [
          "Identifying and integrating best-in-class no- and low-code tools",
          "Streamlining the customer journey through strategic vendor partnerships",
          "Embedding agile practices that increased team capacity by 3+ days per sprint"
        ],
        step: "02"
      },
      {
        title: "Results",
        content: "The MVP launch transformed ORKA's ability to serve commercial solar installers and small businesses. It offered a smooth, digital application experience, boosted customer engagement, and laid the foundation for scalable growth. ORKA's successful acquisition by Sunstone Credit validated both the business and the product approach.",
        step: "03"
      },
      {
        title: "Testimonial",
        content: "Addy is awesome—universally well liked, focused, thoughtful, not swayed by group-think, and a get-shit-done leader. She's serious when needed, and light-hearted as well. I would hire her again to lead Product for just about anything I was building or doing. When we needed to make a major shift in direction, she helped us regroup, refocus, and deliver something our customers truly loved - quickly. Her ability to bring clarity and momentum when it mattered most made a huge difference.",
        author: "Ted Fawcett",
        role: "CEO of ORKA Finance",
        step: "04"
      }
    ]
  }
];